// src/pages/admin/AdminCommunications.jsx
'use client'

import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  collection,
  collectionGroup,
  getDocs,
  orderBy,
  limit,
  query,
  where,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { ArrowPathIcon, ChatBubbleLeftRightIcon, UsersIcon } from '@heroicons/react/24/outline'

// ── Helpers ──────────────────────────────────────────────────────────────────

function tsToMs(ts) {
  if (!ts) return null
  if (typeof ts.toMillis === 'function') return ts.toMillis()
  if (ts?.seconds != null) return ts.seconds * 1000
  if (typeof ts === 'number') return ts
  return null
}

function dayKey(ms) {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

function shortLabel(ms) {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function buildDayBuckets(days) {
  const arr = []
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date()
    d.setDate(d.getDate() - i)
    d.setHours(0, 0, 0, 0)
    arr.push({
      ms:       d.getTime(),
      label:    shortLabel(d.getTime()),
      messages: 0,
      threads:  0,
    })
  }
  return arr
}

function relativeTime(ms) {
  if (!ms) return '—'
  const diff = Date.now() - ms
  if (diff < 60_000) return 'Just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Safely chunk team IDs for Firestore `in` queries (max 30 per batch)
function chunkArray(arr, size) {
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

// ── Custom Tooltip ────────────────────────────────────────────────────────────

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 shadow-xl text-sm">
      <p className="font-bold text-zinc-700 dark:text-zinc-200 mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.fill }} className="font-semibold">
          {p.value.toLocaleString()} {p.dataKey}
        </p>
      ))}
    </div>
  )
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color = 'zinc', icon: Icon, loading }) {
  const palette = {
    blue:   { wrap: 'bg-blue-50  border-blue-100  dark:bg-blue-950/30  dark:border-blue-900',  val: 'text-blue-700  dark:text-blue-300',  sub: 'text-blue-500'   },
    indigo: { wrap: 'bg-indigo-50 border-indigo-100 dark:bg-indigo-950/30 dark:border-indigo-900', val: 'text-indigo-700 dark:text-indigo-300', sub: 'text-indigo-500' },
    green:  { wrap: 'bg-green-50 border-green-100 dark:bg-green-950/30 dark:border-green-900', val: 'text-green-700 dark:text-green-300', sub: 'text-green-600'  },
    violet: { wrap: 'bg-violet-50 border-violet-100 dark:bg-violet-950/30 dark:border-violet-900', val: 'text-violet-700 dark:text-violet-300', sub: 'text-violet-500' },
    zinc:   { wrap: 'bg-zinc-50  border-zinc-100  dark:bg-zinc-900     dark:border-zinc-800',  val: 'text-zinc-800  dark:text-zinc-100',  sub: 'text-zinc-500'   },
  }
  const p = palette[color] || palette.zinc
  return (
    <div className={`rounded-2xl border p-5 ${p.wrap}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-500">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-zinc-300 dark:text-zinc-600" />}
      </div>
      <div className={`text-3xl font-black tabular-nums leading-none ${p.val}`}>
        {loading ? <span className="opacity-40">…</span> : (value ?? 0).toLocaleString()}
      </div>
      {sub && <div className={`text-[10px] mt-1.5 font-semibold ${p.sub}`}>{sub}</div>}
    </div>
  )
}

// ── Activity spark bar (mini inline bar for "last 7d" column) ─────────────────

function Spark({ value, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
        <div
          className="h-full rounded-full bg-blue-400 dark:bg-blue-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400 w-6 text-right">{value}</span>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AdminCommunications() {
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState(30)
  const [refreshKey, setRefreshKey] = useState(0)

  // Raw data from Firestore
  const [chatDocs, setChatDocs]     = useState([])
  const [msgDocs, setMsgDocs]       = useState([])
  const [teamMap, setTeamMap]       = useState({}) // { teamId: teamData }
  const [error, setError]           = useState(null)

  // ── Fetch ────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // 1. All chat thread metadata
      const chatsSnap = await getDocs(
        query(collection(db, 'chats'), orderBy('lastMessageAt', 'desc'), limit(600))
      )
      const chats = chatsSnap.docs.map(d => ({ id: d.id, ...d.data() }))

      // 2. Recent messages via collectionGroup (single-field sort — no composite index needed)
      let msgs = []
      try {
        const msgsSnap = await getDocs(
          query(collectionGroup(db, 'messages'), orderBy('createdAt', 'desc'), limit(3000))
        )
        msgs = msgsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) {
        console.warn('[AdminCommunications] messages collectionGroup failed:', e?.code || e)
        // Fallback: try without orderBy
        try {
          const msgsSnap2 = await getDocs(query(collectionGroup(db, 'messages'), limit(2000)))
          msgs = msgsSnap2.docs.map(d => ({ id: d.id, ...d.data() }))
        } catch {}
      }

      // 3. Collect all team IDs referenced
      const teamIds = new Set()
      chats.forEach(c => { if (Array.isArray(c.teams)) c.teams.forEach(id => teamIds.add(id)) })
      msgs.forEach(m => { if (m.senderTeamId) teamIds.add(m.senderTeamId) })

      // 4. Fetch team docs (batched)
      const tMap = {}
      for (const batch of chunkArray([...teamIds], 30)) {
        try {
          const snap = await getDocs(query(collection(db, 'teams'), where('__name__', 'in', batch)))
          snap.docs.forEach(d => { tMap[d.id] = d.data() })
        } catch {}
      }

      setChatDocs(chats)
      setMsgDocs(msgs)
      setTeamMap(tMap)
    } catch (e) {
      console.error('[AdminCommunications]', e)
      setError(e?.message || 'Failed to load data.')
    } finally {
      setLoading(false)
    }
  }, [refreshKey])  // eslint-disable-line

  useEffect(() => { load() }, [load])

  // ── Derived: Daily chart data ─────────────────────────────────────────────

  const chartData = useMemo(() => {
    const buckets = buildDayBuckets(range)
    const cutoff  = Date.now() - range * 86_400_000
    const byDay   = new Map(buckets.map(b => [b.ms, b]))

    // New threads started in window
    chatDocs.forEach(c => {
      const ms = tsToMs(c.createdAt)
      if (!ms || ms < cutoff) return
      const key = dayKey(ms)
      const bucket = byDay.get(key)
      if (bucket) bucket.threads++
    })

    // Messages sent in window
    msgDocs.forEach(m => {
      const ms = tsToMs(m.createdAt || m.timestamp)
      if (!ms || ms < cutoff) return
      const key = dayKey(ms)
      const bucket = byDay.get(key)
      if (bucket) bucket.messages++
    })

    return buckets
  }, [chatDocs, msgDocs, range])

  // ── Derived: Per-team stats ───────────────────────────────────────────────

  const teamStats = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 86_400_000

    // Threads per team + last active
    const threadsByTeam    = {}
    const lastActiveByTeam = {}

    chatDocs.forEach(c => {
      if (!Array.isArray(c.teams)) return
      const ms = tsToMs(c.lastMessageAt)
      c.teams.forEach(tid => {
        threadsByTeam[tid] = (threadsByTeam[tid] || 0) + 1
        if (ms && (!lastActiveByTeam[tid] || ms > lastActiveByTeam[tid])) {
          lastActiveByTeam[tid] = ms
        }
      })
    })

    // Message counts per team
    const msgTotal = {}
    const msg7d    = {}

    msgDocs.forEach(m => {
      const tid = m.senderTeamId
      if (!tid) return
      const ms = tsToMs(m.createdAt || m.timestamp)
      msgTotal[tid] = (msgTotal[tid] || 0) + 1
      if (ms && ms >= sevenDaysAgo) msg7d[tid] = (msg7d[tid] || 0) + 1
    })

    // Merge all known team IDs
    const allIds = new Set([...Object.keys(threadsByTeam), ...Object.keys(msgTotal)])

    const rows = [...allIds].map(tid => ({
      teamId:      tid,
      name:        teamMap[tid]?.name || teamMap[tid]?.teamName || `Team …${tid.slice(-6)}`,
      logoUrl:     teamMap[tid]?.logoUrl || null,
      threads:     threadsByTeam[tid] || 0,
      msgTotal:    msgTotal[tid] || 0,
      msg7d:       msg7d[tid] || 0,
      lastActiveMs: lastActiveByTeam[tid] || null,
    }))

    rows.sort((a, b) => b.msgTotal - a.msgTotal || b.msg7d - a.msg7d)
    return rows
  }, [chatDocs, msgDocs, teamMap])

  // ── Derived: KPIs ─────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const cutoff     = Date.now() - range * 86_400_000
    const recentMsgs = msgDocs.filter(m => {
      const ms = tsToMs(m.createdAt || m.timestamp)
      return ms && ms >= cutoff
    })
    const activeTeamIds = new Set(recentMsgs.filter(m => m.senderTeamId).map(m => m.senderTeamId))
    const totalThreads  = chatDocs.length
    const avg           = totalThreads ? Math.round(msgDocs.length / totalThreads) : 0
    const peakDay       = chartData.reduce((best, d) => d.messages > best ? d.messages : best, 0)

    return {
      totalThreads,
      recentMessages: recentMsgs.length,
      activeTeams:    activeTeamIds.size,
      avgPerThread:   avg,
      peakDay,
    }
  }, [msgDocs, chatDocs, range, chartData])

  const max7d = useMemo(() => Math.max(...teamStats.map(r => r.msg7d), 1), [teamStats])

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">

      {/* ── Page header ── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-black text-zinc-900 dark:text-white tracking-tight">
            💬 Communications
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
            In-app messaging activity across all teams
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                range === d
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-200'
                  : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-700'
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="p-2 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-all"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── Error state ── */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          ⚠️ {error}
        </div>
      )}

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Threads"
          value={kpis.totalThreads}
          color="blue"
          icon={ChatBubbleLeftRightIcon}
          loading={loading}
        />
        <KpiCard
          label={`Messages (${range}d)`}
          value={kpis.recentMessages}
          color="indigo"
          loading={loading}
        />
        <KpiCard
          label="Active Teams"
          value={kpis.activeTeams}
          color="green"
          icon={UsersIcon}
          sub={`in last ${range} days`}
          loading={loading}
        />
        <KpiCard
          label="Peak Day (msgs)"
          value={kpis.peakDay}
          color="violet"
          sub="highest single day"
          loading={loading}
        />
      </div>

      {/* ── Daily activity chart ── */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-bold text-zinc-900 dark:text-white">Daily Activity</h2>
            <p className="text-xs text-zinc-400 mt-0.5">
              Messages sent per day · last {range} days · in-app chat only
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-500" />
              Messages
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-400" />
              New Threads
            </span>
          </div>
        </div>

        {loading ? (
          <div className="h-56 flex items-center justify-center text-zinc-400 text-sm animate-pulse">
            Loading chart…
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={chartData}
              margin={{ top: 4, right: 4, left: -22, bottom: 0 }}
              barGap={2}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#a1a1aa' }}
                tickLine={false}
                axisLine={false}
                interval={range <= 7 ? 0 : range <= 30 ? 4 : 9}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#a1a1aa' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <Bar dataKey="messages" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={20} />
              <Bar dataKey="threads"  fill="#34d399" radius={[3, 3, 0, 0]} maxBarSize={12} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── SMS / Email notice ── */}
      <div className="rounded-xl border border-amber-100 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-4 flex items-start gap-3">
        <span className="text-lg leading-none mt-0.5">ℹ️</span>
        <div className="text-sm text-amber-800 dark:text-amber-300">
          <strong>SMS &amp; Email columns</strong> below show &quot;—&quot; because outbound SMS and email
          delivery events are not yet stored in Firestore. The table currently tracks{' '}
          <strong>in-app chat messages only</strong>. When those delivery events are added, they will
          populate automatically.
        </div>
      </div>

      {/* ── Team activity table ── */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
          <h2 className="font-bold text-zinc-900 dark:text-white">Team Breakdown</h2>
          <span className="text-xs text-zinc-400">
            {teamStats.length} teams · sorted by total messages
          </span>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-zinc-400 animate-pulse">
            Loading team data…
          </div>
        ) : teamStats.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-zinc-400">
            No messaging activity found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 dark:border-zinc-800">
                  {[
                    { label: '#',          cls: 'w-10 text-left pl-6'    },
                    { label: 'Team',       cls: 'text-left'               },
                    { label: 'Threads',    cls: 'text-center w-24'        },
                    { label: 'Total Msgs', cls: 'text-center w-28'        },
                    { label: 'Last 7d',    cls: 'text-left w-40'          },
                    { label: 'SMS',        cls: 'text-center w-20 opacity-30' },
                    { label: 'Email',      cls: 'text-center w-20 opacity-30' },
                    { label: 'Last Active',cls: 'text-right pr-6 w-32'   },
                  ].map(col => (
                    <th
                      key={col.label}
                      className={`py-3 px-3 text-[10px] font-black uppercase tracking-widest text-zinc-400 ${col.cls}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800/60">
                {teamStats.map((row, i) => (
                  <tr
                    key={row.teamId}
                    className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors group"
                  >
                    {/* Rank */}
                    <td className="py-3.5 pl-6 pr-3 text-xs text-zinc-300 dark:text-zinc-600 tabular-nums">
                      {i + 1}
                    </td>

                    {/* Team */}
                    <td className="py-3.5 px-3">
                      <div className="flex items-center gap-3">
                        {row.logoUrl ? (
                          <img
                            src={row.logoUrl}
                            alt=""
                            className="h-9 w-9 rounded-xl object-cover flex-shrink-0 ring-1 ring-zinc-100 dark:ring-zinc-800"
                          />
                        ) : (
                          <div className="h-9 w-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-black text-blue-500 dark:text-blue-400">
                              {row.name.slice(0, 2).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="font-semibold text-zinc-900 dark:text-zinc-100 leading-tight">
                            {row.name}
                          </div>
                          <div className="text-[10px] text-zinc-400 font-mono tracking-tight">
                            {row.teamId.slice(-8)}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Threads */}
                    <td className="py-3.5 px-3 text-center">
                      <span className="inline-flex items-center justify-center h-6 w-8 rounded-md bg-zinc-100 dark:bg-zinc-800 text-xs font-bold text-zinc-600 dark:text-zinc-300 tabular-nums">
                        {row.threads}
                      </span>
                    </td>

                    {/* Total Messages */}
                    <td className="py-3.5 px-3 text-center">
                      <span
                        className={`text-base font-black tabular-nums ${
                          row.msgTotal > 50 ? 'text-blue-600 dark:text-blue-400'
                          : row.msgTotal > 10 ? 'text-indigo-500 dark:text-indigo-400'
                          : row.msgTotal > 0 ? 'text-zinc-500 dark:text-zinc-400'
                          : 'text-zinc-300 dark:text-zinc-600'
                        }`}
                      >
                        {row.msgTotal.toLocaleString()}
                      </span>
                    </td>

                    {/* Last 7d — spark bar */}
                    <td className="py-3.5 px-3">
                      {row.msg7d > 0 ? (
                        <Spark value={row.msg7d} max={max7d} />
                      ) : (
                        <span className="text-xs text-zinc-300 dark:text-zinc-700">No activity</span>
                      )}
                    </td>

                    {/* SMS (not tracked) */}
                    <td className="py-3.5 px-3 text-center text-zinc-200 dark:text-zinc-700 text-xs">—</td>

                    {/* Email (not tracked) */}
                    <td className="py-3.5 px-3 text-center text-zinc-200 dark:text-zinc-700 text-xs">—</td>

                    {/* Last Active */}
                    <td className="py-3.5 pl-3 pr-6 text-right">
                      <span className={`text-xs tabular-nums font-medium ${
                        row.lastActiveMs && Date.now() - row.lastActiveMs < 86_400_000
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-zinc-400 dark:text-zinc-500'
                      }`}>
                        {relativeTime(row.lastActiveMs)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>

              {/* Footer summary */}
              <tfoot>
                <tr className="border-t-2 border-zinc-100 dark:border-zinc-800 bg-zinc-50/80 dark:bg-zinc-900">
                  <td colSpan={2} className="py-3 pl-6 text-xs font-bold text-zinc-500">
                    Totals
                  </td>
                  <td className="py-3 px-3 text-center text-xs font-bold text-zinc-600 dark:text-zinc-300 tabular-nums">
                    {chatDocs.length.toLocaleString()}
                  </td>
                  <td className="py-3 px-3 text-center text-xs font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                    {msgDocs.length.toLocaleString()}
                  </td>
                  <td colSpan={4} className="py-3 px-3 text-xs text-zinc-400">
                    {teamStats.filter(r => r.msg7d > 0).length} teams active in last 7 days
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
